import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Modal, Select, Switch, theme } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import { Template } from '@totem/components/ticketing/templateManagement/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';
import { TICKETS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

import '../../ticketing.css';

type TemplateFormValues = {
  name: string;
  ticketType: string;
  category: string;
  subCategory: string;
  services: string[];
  active: boolean;
  autoApply: boolean;
};

const TemplateManagementListPanel = () => {
  const { token } = theme.useToken();
  const { getApiToken, userProfile } = useContext(UserProfileContext);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm<TemplateFormValues>();

  const fetchTemplates = useCallback(() => {
    setLoading(true);

    fetch(`${TICKETS_ENDPOINT}/templates`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: Template[]) => {
        if (isNotNull(result)) {
          setTemplates(result);
        } else {
          setTemplates([]);
        }
      })
      .finally(() => setLoading(false));
  }, [getApiToken]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreateTemplate = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);
      const now = dayjs().toISOString();
      const payload: Template = {
        id: EMPTY_ID,
        name: values.name,
        ticketType: values.ticketType,
        category: values.category,
        subCategory: values.subCategory,
        services: values.services || [],
        active: values.active ?? true,
        autoApply: values.autoApply ?? false,
        tasks: [],
        createdByName: userProfile.email,
        createdById: userProfile.id,
        createdAt: now,
        updatedByName: userProfile.email,
        updatedById: userProfile.id,
        updatedAt: now,
      };

      fetch(`${TICKETS_ENDPOINT}/templates`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      }).then((res) => {
        setIsSaving(false);
        const success = CheckResponseShowError(res);
        if (success) {
          setShowCreateModal(false);

          form.resetFields();

          fetchTemplates();
        }
      });
    } catch {
      setIsSaving(false);
    }
  };

  const uniqueValues = useCallback(
    (field: keyof Template) =>
      [
        ...new Set(
          templates.map((tpl) => tpl[field] as string).filter(Boolean),
        ),
      ].map((val) => ({ text: val, value: val })),
    [templates],
  );

  const columns: ColumnProps<Template>[] = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (rec, cmp) => (rec.name || '').localeCompare(cmp.name || ''),
        filters: uniqueValues('name'),
        filterSearch: true,
        onFilter: (value, record) => record.name === value,
        render: (_, template: Template) => (
          <Link to={`/dashboard/ticketingTemplate/${template.id}`}>
            {template.name}
          </Link>
        ),
      },
      {
        title: 'Ticket Type',
        dataIndex: 'ticketType',
        key: 'ticketType',
        sorter: (rec, cmp) =>
          (rec.ticketType || '').localeCompare(cmp.ticketType || ''),
        filters: uniqueValues('ticketType'),
        filterSearch: true,
        onFilter: (value, record) => record.ticketType === value,
      },
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        sorter: (rec, cmp) =>
          (rec.category || '').localeCompare(cmp.category || ''),
        filters: uniqueValues('category'),
        filterSearch: true,
        onFilter: (value, record) => record.category === value,
      },
      {
        title: 'Sub Category',
        dataIndex: 'subCategory',
        key: 'subCategory',
        sorter: (rec, cmp) =>
          (rec.subCategory || '').localeCompare(cmp.subCategory || ''),
        filters: uniqueValues('subCategory'),
        filterSearch: true,
        onFilter: (value, record) => record.subCategory === value,
      },
      {
        title: 'Services',
        dataIndex: 'services',
        key: 'services',
        render: (_, template: Template) =>
          template.services && template.services.length > 0
            ? template.services.join(', ')
            : '-',
      },
      {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        sorter: (rec, cmp) => Number(rec.active) - Number(cmp.active),
        filters: [
          { text: 'Active', value: true },
          { text: 'Inactive', value: false },
        ],
        onFilter: (value, record) => record.active === value,
        render: (_, template: Template) =>
          template.active ? (
            <CheckCircleOutlined style={{ color: token.colorSuccess }} />
          ) : (
            <MinusCircleOutlined style={{ color: token.colorError }} />
          ),
      },
      {
        title: 'Auto Apply',
        dataIndex: 'autoApply',
        key: 'autoApply',
        sorter: (rec, cmp) => Number(rec.autoApply) - Number(cmp.autoApply),
        filters: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        onFilter: (value, record) => record.autoApply === value,
        render: (_, template: Template) =>
          template.autoApply ? (
            <CheckCircleOutlined style={{ color: token.colorSuccess }} />
          ) : (
            <MinusCircleOutlined style={{ color: token.colorError }} />
          ),
      },
      {
        title: 'Created By',
        dataIndex: 'createdByName',
        key: 'createdByName',
      },
      {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (_, template: Template) => formatDateOnly(template.updatedAt),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, template: Template) => (
          <div className="center-table-cell">
            <IBTooltip content="Edit Template">
              <Link to={`/dashboard/ticketingTemplate/${template.id}`}>
                <Button icon={<EditOutlined />} shape="circle" />
              </Link>
            </IBTooltip>
          </div>
        ),
      },
    ],
    [uniqueValues, token],
  );

  return (
    <>
      <IBCard
        isCovered
        title={'Task Template Management'}
        extra={
          <>
            <RecordCountBadge count={templates.length} />
            &nbsp;&nbsp;&nbsp;
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowCreateModal(true)}
            >
              New Template
            </Button>
          </>
        }
      >
        <IBTable<Template>
          showSorterTooltip
          columns={columns}
          dataSource={templates}
          loading={loading}
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
          }}
          rowKey={(record) => record.id}
        />
      </IBCard>

      <Modal
        title="Create Template"
        open={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onOk={handleCreateTemplate}
        okText="Create"
        confirmLoading={isSaving}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required.' }]}
          >
            <Input placeholder="Name" />
          </Form.Item>
          <Form.Item label="Ticket Type" name="ticketType">
            <Input placeholder="Ticket Type" />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Category is required.' }]}
          >
            <Input placeholder="Category" />
          </Form.Item>
          <Form.Item
            label="Sub Category"
            name="subCategory"
            rules={[{ required: true, message: 'Sub category is required.' }]}
          >
            <Input placeholder="Sub category" />
          </Form.Item>
          <Form.Item label="Services" name="services">
            <Select
              mode="tags"
              placeholder="Add service tags"
              tokenSeparators={[',']}
            />
          </Form.Item>
          <Form.Item
            label="Active"
            name="active"
            valuePropName="checked"
            initialValue
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Auto Apply"
            name="autoApply"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TemplateManagementListPanel;
